import { useEffect, useState } from "react";
import { Bookmark, ChevronLeft, ChevronRight, Heart, MessageCircle, Play, Repeat2, Send, Share2, ThumbsUp } from "lucide-react";
import { platformLabel } from "./socialMarketingApi";

function mediaKind(url) {
  const path = String(url || "").split("?")[0].split("#")[0].toLowerCase();
  if (/\.(mp4|webm|mov|m4v|ogg)$/.test(path)) return "video";
  return "image";
}

function MediaStage({ urls, aspect, rounded = false, dark = false }) {
  const [index, setIndex] = useState(0);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setIndex(0);
    setBroken(false);
  }, [urls.join("|")]);

  if (!urls.length) {
    return (
      <div className={`flex items-center justify-center border border-dashed border-slate-300 bg-slate-100 px-4 text-center text-xs text-slate-500 ${aspect} ${rounded ? "rounded-2xl" : ""}`}>
        Nessuna immagine o video per questo canale
      </div>
    );
  }

  const safeIndex = Math.min(index, urls.length - 1);
  const url = urls[safeIndex];
  const kind = mediaKind(url);
  const showNav = urls.length > 1;
  const showMedia = (itemIndex) => {
    setIndex(itemIndex);
    setBroken(false);
  };
  return (
    <div className={`relative overflow-hidden bg-slate-200 ${aspect} ${rounded ? "rounded-2xl" : ""}`}>
      {broken ? (
        <div className="flex h-full items-center justify-center px-4 text-center text-xs text-slate-500">Media non caricabile da questo indirizzo</div>
      ) : kind === "video" ? (
        <video key={url} src={url} controls playsInline className="h-full w-full bg-black object-contain" onError={() => setBroken(true)} />
      ) : (
        <img key={url} src={url} alt="" className="h-full w-full object-cover" onError={() => setBroken(true)} />
      )}
      {showNav ? (
        <>
          <button
            type="button"
            aria-label="Media precedente"
            disabled={safeIndex <= 0}
            className="absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white shadow-md enabled:hover:bg-black/80 disabled:cursor-default disabled:opacity-35"
            onClick={() => showMedia(safeIndex - 1)}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Media successivo"
            disabled={safeIndex >= urls.length - 1}
            className="absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white shadow-md enabled:hover:bg-black/80 disabled:cursor-default disabled:opacity-35"
            onClick={() => showMedia(safeIndex + 1)}
          >
            <ChevronRight className="h-5 w-5" aria-hidden />
          </button>
        </>
      ) : null}
      {showNav ? (
        <div className={`absolute inset-x-0 z-10 flex justify-center gap-1.5 ${kind === "video" ? "bottom-11" : "bottom-2"}`}>
          {urls.map((item, itemIndex) => (
            <button
              key={`${item}-${itemIndex}`}
              type="button"
              aria-label={`Media ${itemIndex + 1}`}
              className={`h-2.5 w-2.5 rounded-full shadow-sm ${itemIndex === safeIndex ? "bg-white" : "bg-white/55"}`}
              onClick={() => showMedia(itemIndex)}
            />
          ))}
        </div>
      ) : null}
      {dark && kind === "image" && !broken ? <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" /> : null}
    </div>
  );
}

function Avatar({ name, className = "" }) {
  const letter = (name || "A").trim().charAt(0).toUpperCase();
  return (
    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-800 text-sm font-semibold text-white ${className}`} aria-hidden>
      {letter}
    </span>
  );
}

function Caption({ text, className = "" }) {
  return <p className={`whitespace-pre-wrap break-words text-sm leading-relaxed ${className}`}>{text || "Testo del post ancora vuoto."}</p>;
}

function Header({ account, network }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <Avatar name={account} />
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-slate-900">{account}</div>
        <div className="text-xs text-slate-500">{network}</div>
      </div>
    </div>
  );
}

export default function SocialChannelPreview({ platform, account, content, mediaUrls, assigned }) {
  const network = platformLabel(platform, platform);
  const urls = (mediaUrls || []).filter(Boolean);
  const shell = "mx-auto w-full max-w-[22rem] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm";

  let body = null;
  if (platform === "instagram") {
    body = (
      <div className={shell}>
        <Header account={account} network="Instagram" />
        <MediaStage urls={urls} aspect="aspect-square" />
        <div className="flex gap-3 px-3 py-2 text-slate-800">
          <Heart className="h-5 w-5" aria-hidden />
          <MessageCircle className="h-5 w-5" aria-hidden />
          <Send className="h-5 w-5" aria-hidden />
          <Bookmark className="ml-auto h-5 w-5" aria-hidden />
        </div>
        <div className="px-3 pb-3"><Caption text={content} /></div>
      </div>
    );
  } else if (platform === "facebook") {
    body = (
      <div className={shell}>
        <Header account={account} network="Facebook" />
        <div className="px-3 pb-3"><Caption text={content} /></div>
        <MediaStage urls={urls} aspect="aspect-video" />
        <div className="grid grid-cols-3 border-t border-slate-200 text-center text-xs font-medium text-slate-600">
          <span className="inline-flex items-center justify-center gap-1 py-2"><ThumbsUp className="h-3.5 w-3.5" aria-hidden /> Mi piace</span>
          <span className="inline-flex items-center justify-center gap-1 py-2"><MessageCircle className="h-3.5 w-3.5" aria-hidden /> Commenta</span>
          <span className="inline-flex items-center justify-center gap-1 py-2"><Share2 className="h-3.5 w-3.5" aria-hidden /> Condividi</span>
        </div>
      </div>
    );
  } else if (platform === "linkedin") {
    body = (
      <div className={shell}>
        <Header account={account} network="LinkedIn" />
        <div className="px-3 pb-3"><Caption text={content} /></div>
        <MediaStage urls={urls} aspect="aspect-video" />
        <div className="border-t border-slate-200 px-3 py-2 text-xs font-medium text-sky-800">Consiglia · Commenta · Diffondi</div>
      </div>
    );
  } else if (platform === "x") {
    body = (
      <div className={shell}>
        <div className="flex gap-2 p-3">
          <Avatar name={account} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900">{account} <span className="font-normal text-slate-500">· X</span></div>
            <Caption text={content} />
            <div className="mt-3"><MediaStage urls={urls} aspect="aspect-video" rounded /></div>
            <div className="mt-2 flex gap-4 text-slate-500">
              <MessageCircle className="h-4 w-4" aria-hidden />
              <Repeat2 className="h-4 w-4" aria-hidden />
              <Heart className="h-4 w-4" aria-hidden />
              <Share2 className="h-4 w-4" aria-hidden />
            </div>
          </div>
        </div>
      </div>
    );
  } else if (platform === "tiktok") {
    body = (
      <div className="relative mx-auto aspect-[9/16] w-full max-w-[18rem] overflow-hidden rounded-2xl bg-black text-white shadow-sm">
        <MediaStage urls={urls} aspect="h-full" dark />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 p-3">
          <div className="text-sm font-semibold">{account}</div>
          <p className="mt-1 line-clamp-4 whitespace-pre-wrap text-xs leading-relaxed">{content || "Testo del post ancora vuoto."}</p>
        </div>
        {urls.length && mediaKind(urls[0]) !== "video" ? (
          <Play className="pointer-events-none absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 text-white/80" aria-hidden />
        ) : null}
      </div>
    );
  } else if (platform === "youtube") {
    body = (
      <div className={shell}>
        <div className="relative">
          <MediaStage urls={urls} aspect="aspect-video" />
          {!urls.length || mediaKind(urls[0]) !== "video" ? (
            <Play className="pointer-events-none absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-600 p-2 text-white" aria-hidden />
          ) : null}
        </div>
        <div className="space-y-1 p-3">
          <div className="line-clamp-2 text-sm font-semibold text-slate-900">{content.split("\n").find((line) => line.trim()) || "Titolo del video"}</div>
          <div className="text-xs text-slate-500">{account} · YouTube</div>
        </div>
      </div>
    );
  } else if (platform === "pinterest") {
    body = (
      <div className={`${shell} max-w-[16rem]`}>
        <MediaStage urls={urls} aspect="aspect-[3/4]" />
        <div className="p-3"><Caption text={content} /></div>
      </div>
    );
  } else {
    body = (
      <div className={shell}>
        <Header account={account} network={network || "Social"} />
        <div className="px-3 pb-3"><Caption text={content} /></div>
        <MediaStage urls={urls} aspect="aspect-square" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-admin-text">Anteprima {network}</div>
      {assigned ? null : (
        <p className="text-xs text-admin-muted">Questo caricamento non è assegnato a {network}. L'anteprima usa il testo attuale.</p>
      )}
      {body}
    </div>
  );
}
